import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';
import type {
  MessengerConversation,
  MessengerMessage,
  MessengerAgentConfig,
  InboxStats,
  ConversationStatus,
} from '@/types/outreach';

// --- Conversations ---

interface ConversationsResponse {
  count: number;
  results: MessengerConversation[];
}

export const useMessengerConversations = (search?: string, status?: ConversationStatus | '') => {
  return useInfiniteQuery({
    queryKey: ['messenger-conversations', search, status],
    queryFn: async ({ pageParam = 1 }) => {
      const params: Record<string, string> = { page: String(pageParam), page_size: '20' };
      if (search) params.search = search;
      if (status) params.status = status;
      const { data } = await api.get<ConversationsResponse>(API_ROUTES.messengerConversations, {
        params,
      });
      return data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.results.length, 0);
      if (loaded >= lastPage.count) return undefined;
      return allPages.length + 1;
    },
    initialPageParam: 1,
    refetchInterval: 5000,
  });
};

export const useMessengerConversationDetail = (id: string | null) => {
  return useQuery({
    queryKey: ['messenger-conversation', id],
    queryFn: async () => {
      const { data } = await api.get<MessengerConversation>(
        `${API_ROUTES.messengerConversations}${id}/`,
      );
      return data;
    },
    enabled: !!id,
  });
};

interface MessagesResponse {
  count: number;
  results: MessengerMessage[];
}

export const useMessengerConversationMessages = (id: string | null, after?: string) => {
  return useQuery({
    queryKey: ['messenger-messages', id, after],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (after) params.after = after;
      const { data } = await api.get<MessagesResponse>(
        `${API_ROUTES.messengerConversations}${id}/messages/`,
        { params },
      );
      return data;
    },
    enabled: !!id,
    refetchInterval: 2500,
  });
};

// --- Mutations ---

export const useMessengerSendAdminMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationId,
      content,
    }: {
      conversationId: string;
      content: string;
    }) => {
      const { data } = await api.post<MessengerMessage>(
        `${API_ROUTES.messengerConversations}${conversationId}/send/`,
        { content },
      );
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['messenger-messages', variables.conversationId] });
      queryClient.invalidateQueries({ queryKey: ['messenger-conversations'] });
    },
  });
};

export const useMessengerUpdateConversationStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationId,
      status,
    }: {
      conversationId: string;
      status: ConversationStatus;
    }) => {
      const { data } = await api.patch(
        `${API_ROUTES.messengerConversations}${conversationId}/status/`,
        { status },
      );
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['messenger-conversation', variables.conversationId],
      });
      queryClient.invalidateQueries({ queryKey: ['messenger-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['messenger-inbox-stats'] });
    },
  });
};

export const useMessengerMarkConversationRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      const { data } = await api.post(
        `${API_ROUTES.messengerConversations}${conversationId}/read/`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messenger-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['messenger-inbox-stats'] });
    },
  });
};

// --- Inbox Stats ---

export const useMessengerInboxStats = () => {
  return useQuery({
    queryKey: ['messenger-inbox-stats'],
    queryFn: async () => {
      const { data } = await api.get<InboxStats>(API_ROUTES.messengerInboxStats);
      return data;
    },
    refetchInterval: 10000,
  });
};

// --- Agent Config ---

export const useMessengerAgentConfigs = () => {
  return useQuery({
    queryKey: ['messenger-agent-configs'],
    queryFn: async () => {
      const { data } = await api.get<MessengerAgentConfig[]>(API_ROUTES.messengerAgentConfig);
      return data;
    },
  });
};

export const useMessengerSaveAgentConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      config: Partial<MessengerAgentConfig> & { name: string; systemPrompt: string },
    ) => {
      if (config.id) {
        const { data } = await api.put<MessengerAgentConfig>(
          `${API_ROUTES.messengerAgentConfig}${config.id}/`,
          config,
        );
        return data;
      }
      const { data } = await api.post<MessengerAgentConfig>(
        API_ROUTES.messengerAgentConfig,
        config,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messenger-agent-configs'] });
    },
  });
};

export const useMessengerDeleteAgentConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${API_ROUTES.messengerAgentConfig}${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messenger-agent-configs'] });
    },
  });
};
